import { useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { todayInRome } from 'src/utils/get-date-worlds-format';

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

import { paths } from 'src/routes/paths';

import {
  sendExciseReportEmail,
  useGetAdministrationStats,
  useGetAgingPayables,
  useGetAgingReceivables,
  useGetLogisticsStats,
} from 'src/api/admin';

import { DashboardPeriod } from 'src/types';

import { useLocales } from 'src/locales';
import { CustomBreadcrumbs } from 'src/components/custom-breadcrumbs/custom-breadcrumbs';
import { PageContainer } from 'src/components/page-container';
import { useSnackbar } from 'src/components/snackbar';

import LogisticsSection from '../logistics/logistics-section';
import InvoicingSection from '../invoicing/invoicing-section';

import MonthlyStatsCards from '../thermometer/monthly-stats-cards';
import { formatSpan, PERIOD_LABEL_KEY, PERIOD_UNAVAILABLE_KEY } from '../thermometer/period';
import { useTermometroPeriod } from '../thermometer/use-termometro-period';
import ReceivablesSection from '../receivables/receivables-section';
import PayablesSection from '../payables/payables-section';
import CashCycleSection from '../cash-cycle/cash-cycle-section';
import SlowStockSection from '../slow-stock/slow-stock-section';
import AdminNotificationsBox from '../notifications/admin-notifications-box';

/** Ties the period toggle to the panel it controls, for `aria-controls`. */
const THERMOMETER_PANEL_ID = 'termometro-panel';
/** The panel's accessible name — the heading already on screen, rather than a copy of it. */
const THERMOMETER_HEADING_ID = 'termometro-heading';

export default function OperationsDashboardView() {
  const { t, currentLang } = useLocales('operativaDashboard');
  const { t: tSlowStock } = useLocales('slowStockDashboard');
  const { enqueueSnackbar } = useSnackbar();

  const currentDate = todayInRome();
  const [exciseReportDate, setExciseReportDate] = useState<Dayjs | null>(dayjs(currentDate));
  // NAT-2334 — month-to-date is the default because it is the span this page has always shown,
  // and the one an operator reads daily. Deliberately not persisted: a stale year-to-date left
  // over from a previous visit would be read as this month at a glance.
  const [period, setPeriod] = useState<DashboardPeriod>('mtd');

  const { data: logisticsData } = useGetLogisticsStats({ currentDate });
  const { data: administrationData } = useGetAdministrationStats();
  // NAT-2545 — the served-vs-selected rules live in `useTermometroPeriod`, shared with
  // «Situazione attuale», so the two pages cannot disagree about the same payload.
  const {
    data: currentMonthData,
    error: currentMonthError,
    servedPeriod,
    isBusy,
    periodUnavailable,
  } = useTermometroPeriod(period);
  const { data: receivablesData, refetch: refetchReceivables } = useGetAgingReceivables();
  const { data: payablesData } = useGetAgingPayables();

  // What the figures actually cover, in words. The dates are preferred, but a backend that
  // predates `?period=` sends no `window` either — which is EXACTLY the state that raises the
  // warning below, so pointing that warning at a caption which is guaranteed absent would name
  // nothing at all. Naming the served period always says something true.
  const servedSpan =
    formatSpan(currentMonthData?.window, currentLang.value) ?? t(PERIOD_LABEL_KEY[servedPeriod]);

  const emailExciseReport = async () => {
    try {
      const dateToSend = exciseReportDate ? exciseReportDate.format('YYYY-MM-DD') : currentDate;
      await sendExciseReportEmail({ date: dateToSend });
      enqueueSnackbar(t('excise_report_sent'), { variant: 'success' });
    } catch (error) {
      const message = error?.response?.data?.message || error.message;
      enqueueSnackbar(message, { variant: 'error' });
    }
  };

  return (
    <PageContainer variant="data">
      <CustomBreadcrumbs
        heading={t('operativa')}
        links={[{ name: t('admin'), href: paths.admin.root }, { name: t('operativa') }]}
        sx={{ mb: [3, 3, 5] }}
      />

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 6,
        }}
      >
        {/* NAT-2562 — above everything else: problems an admin must see. Loads its own data and
            renders nothing when there is nothing to show. */}
        <AdminNotificationsBox />

        {/* Section 1 — Termometro */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
            }}
          >
            <Typography variant="h4" id={THERMOMETER_HEADING_ID}>
              {t('termometro')}
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: 1.5,
              }}
            >
              {/* The busy signal lives HERE rather than as opacity on the cards. Dimming the
                  grid to 50% took the card labels from 4.51:1 contrast to 1.92:1 — a loading
                  hint is not worth making the figures unreadable to reach it. */}
              {isBusy && <CircularProgress size={16} aria-hidden />}
              <ToggleButtonGroup
                exclusive
                size="small"
                color="primary"
                value={period}
                aria-label={t('periodo')}
                // Omitted when the panel is not on the page (first load, or an error with no
                // cached data) — pointing at an id that does not exist is worse than not pointing.
                aria-controls={currentMonthData ? THERMOMETER_PANEL_ID : undefined}
                // Typed explicitly: MUI declares this callback's value as `any`, so a typo in a
                // ToggleButton value below would type-check, ship `?period=yt` and earn a 400.
                onChange={(_event, value: DashboardPeriod | null) => {
                  // MUI reports null when the active button is clicked again. Honouring it would
                  // leave the cards on screen with no period selected.
                  if (!value) return;
                  setPeriod(value);
                }}
              >
                <ToggleButton value={'mtd' satisfies DashboardPeriod}>
                  {t('mese_corrente')}
                </ToggleButton>
                <ToggleButton value={'ytd' satisfies DashboardPeriod}>
                  {t('anno_corrente')}
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Box>

          {/* The dim and the spinner are both invisible to a screen reader. This is the only
              thing that announces the span changing, and it announces the span that ARRIVED. */}
          <Box
            role="status"
            aria-live="polite"
            sx={{
              // '1px' as strings, NOT 1: MUI's sizing transform maps a bare `1` to '100%', so
              // this was a full-size absolutely-positioned box held invisible by `clip` alone —
              // one step from becoming a click shield over the dashboard.
              position: 'absolute',
              width: '1px',
              height: '1px',
              margin: '-1px',
              padding: 0,
              border: 0,
              overflow: 'hidden',
              clipPath: 'inset(50%)',
              whiteSpace: 'nowrap',
            }}
          >
            {/* Named, not a bare date range: this announces unprompted when the first response
                lands, and "1 gen 2026 – 26 ago 2026" alone says nothing on a five-section page. */}
            {`${t('termometro')}: ${isBusy ? t('caricamento') : servedSpan}`}
          </Box>
          {/* Read in preference to stale figures. `keepPreviousData` means a failed request no
              longer blanks this section, so without this the previous span's numbers would sit
              here undimmed with nothing on screen saying the fetch failed. */}
          {currentMonthError && <Alert severity="error">{t('caricamento_non_riuscito')}</Alert>}
          {periodUnavailable && !currentMonthError && (
            // `role="status"` rather than MUI's default `role="alert"`: an alert is an
            // ASSERTIVE live region and interrupts whatever is being read. An error earns that;
            // "the span you picked isn't available" does not.
            <Alert severity="warning" role="status">
              {t(PERIOD_UNAVAILABLE_KEY[servedPeriod])}
            </Alert>
          )}
          {currentMonthData && (
            <MonthlyStatsCards
              currentMonthData={currentMonthData}
              id={THERMOMETER_PANEL_ID}
              labelledBy={THERMOMETER_HEADING_ID}
            />
          )}
        </Box>

        {/* Section 2 — Logistica */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
            overflowX: 'auto',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Typography variant="h4">{t('logistica')}</Typography>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <DatePicker
                label={t('excise_report_date')}
                value={exciseReportDate}
                onChange={(newValue) => setExciseReportDate(newValue)}
                slotProps={{ textField: { size: 'small' } }}
              />
              <Button onClick={emailExciseReport} variant="contained">
                {t('email_excise_report')}
              </Button>
            </Box>
          </Box>
          {logisticsData && <LogisticsSection {...logisticsData} />}
        </Box>

        {/* Section 3 — Da fatturare */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
            overflowX: 'auto',
          }}
        >
          <Typography variant="h4">{t('da_fatturare')}</Typography>
          {administrationData && <InvoicingSection administrationData={administrationData} />}
        </Box>

        {/* Section 4 — Da incassare */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
          }}
        >
          <Typography variant="h4">{t('da_incassare')}</Typography>
          {receivablesData?.buckets && (
            <ReceivablesSection data={receivablesData} onActivityLogged={refetchReceivables} />
          )}
        </Box>

        {/* Section 5 — Da pagare */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
          }}
        >
          <Typography variant="h4">{t('da_pagare')}</Typography>
          {payablesData?.buckets && <PayablesSection data={payablesData} />}
        </Box>

        {/* Section 6 — Ciclo di cassa (NAT-2519). Loads its own data, heading included. */}
        <CashCycleSection />

        {/* Section 7 — Magazzino fermo (NAT-2522). Loads its own data, heading included. */}
        <SlowStockSection exportTitle={`${t('operativa')} – ${tSlowStock('titolo')}`} />
      </Box>
    </PageContainer>
  );
}
